// @flow
import * as React from 'react'
import KeyboardSelectable from '../KeyboardSelectable'
import AutofocusElement from '../AutofocusElement'
import { InputContainer, Footer } from './elements'
import Lightbox from '../Lightbox'
import BaseField from '../BaseField'
import Input from '../Input'

// Types
import type { Props as BaseFieldProps, Input as InputProps } from '../BaseField'
import type {
  Appearance as LightboxAppearance,
  AppearanceTitle,
} from '../Lightbox'

export type Props = BaseFieldProps & {
  input: InputProps,
  showed?: boolean,
  fieldAppearance?: LightboxAppearance,
  renderLightbox: (RenderProps) => ?React.Node,
  renderValue: (any, (any) => React.Node) => React.Node,
  renderContainer: (props: RenderContainerProps) => React.Node,
  lightboxTitle?: ?AppearanceTitle,
  lightboxSubtitle?: ?AppearanceTitle,
}

type RenderProps = {
  setFocusableRef: Function,
  appearance: LightboxAppearance,
  select: (any) => any,
  cancel: () => any,
}

type RenderContainerProps = {
  renderInput: Function,
  openLightbox: Function,
  props: Props,
}

/**
 * A component to create a fields that uses lightbox to enter the exact field
 * value. A good example of such field is `DesktopDateField`, which shows the
 * lightbox to the user when he click the field.
 * @extends React
 */
export class LightboxBaseField extends React.Component<Props> {
  static defaultProps = {
    input: {},
    meta: {},
    disableTime: true,
    renderValue: (val: any, render: any) => render(val),
    renderContainer: ({
      renderInput,
      openLightbox,
      props,
    }: RenderContainerProps) => (
      <BaseField
        {...props}
        input={{
          ...props.input,
          onClick: openLightbox,
        }}
        type="static"
        renderInput={renderInput}
        selectable
      />
    ),
  }

  lightboxController = null

  inputDOM = null

  handleClickCancel = ({
    closeManually = true,
  }: { closeManually: boolean } = {}) => {
    if (closeManually) {
      this.lightboxController && this.lightboxController.close()
    }
  }

  handleCloseLightbox = () => {
    this.handleClickCancel({ closeManually: false })
    this.inputDOM && this.inputDOM.focus()
  }

  handleInputClick = () => {
    this.lightboxController && this.lightboxController.open()
  }

  handleSelect = (value: any) => {
    const {
      input: { onChange },
    } = this.props

    onChange && onChange(value)
    this.handleClickCancel()
  }

  renderLightbox = () => {
    const {
      lightboxSubtitle,
      lightboxTitle,
      label,
      placeholder,
      showed,
    } = this.props

    const title = lightboxTitle !== null ? lightboxTitle || label : null
    const subtitle =
      lightboxSubtitle !== null ? lightboxSubtitle || placeholder : null

    return (
      <Lightbox
        showed={showed}
        title={title}
        subtitle={subtitle}
        controllerRef={(c) => (this.lightboxController = c)}
        render={this.renderLightboxContent}
        appearance={this.props.fieldAppearance}
        onClose={this.handleCloseLightbox}
      />
    )
  }

  renderLightboxContent = ({ appearance }: *) => {
    return (
      <AutofocusElement
        render={({ setFocusableRef }) =>
          this.props.renderLightbox({
            appearance,
            setFocusableRef,
            select: this.handleSelect,
            cancel: this.handleClickCancel,
          })
        }
      />
    )
  }

  renderInput = ({
    input: { value, onFocus, onBlur, onClick },
    style,
    props,
  }: any) => {
    return (
      <InputContainer>
        <KeyboardSelectable
          render={({ keyDownHandler }) =>
            this.props.renderValue(value, (renderedValue) => (
              <Input
                style={style}
                props={props}
                input={{
                  value: renderedValue,
                  onKeyDown: keyDownHandler(this.handleInputClick),
                  onClick,
                  onFocus,
                  onBlur: (e) => {
                    if (
                      !this.lightboxController ||
                      !this.lightboxController.showed
                    ) {
                      onBlur(e)
                    }
                  },
                }}
                innerRef={(c) => (this.inputDOM = c)}
              />
            ))
          }
        />
      </InputContainer>
    )
  }

  render() {
    return (
      <>
        {this.props.renderContainer({
          openLightbox: this.handleInputClick,
          renderInput: this.renderInput,
          props: this.props,
        })}
        {this.renderLightbox()}
      </>
    )
  }
}

export { Footer }
export default LightboxBaseField
